import { IBaseApiRes } from '@interfaces/generic.interface';
import {
  IPOSAllShift,
  IPOSDeclareNewDay,
  IPOSPostEndShift,
  IPOSPostStartShift,
} from '@modules/point-of-sale/interface/pos-work-period.interface';
import { IPOSGetShiftID } from '@modules/point-of-sale/interface/station-menu.interface';
import {
  clearPOSMasterlist,
  IPOSShift,
  setPOSShifts,
} from '@modules/point-of-sale/slice/pos-masterlist.slice';
import {
  addPOSInitialShift,
  clearPOSStation,
  removePOSStationEntry,
  StationEntry,
} from '@modules/point-of-sale/slice/pos-stations.slice';
import { clearPOSTableArea } from '@modules/point-of-sale/slice/pos-table-area.slice';
import { api } from '@services/api';
import { RootState } from '@store/index';
import { onClose, onOpen } from '@store/slices/disclosure.slice';
import { setSalesDate } from '@store/slices/app.slice';
import { handleError } from '@utils/api-error-handling';
import { encryptPayload } from '@utils/security';

export const posWorkPeriodAPI = api
  .enhanceEndpoints({
    addTagTypes: ['Shifts', 'Shift'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getShift: builder.query({
        query: () => `shifts/work-periods`,
        providesTags: ['Shifts'],
        async onQueryStarted(_, { queryFulfilled, dispatch, getState }) {
          const { data } = await queryFulfilled;

          if (!data) return;

          const state = getState() as RootState;
          const stationName = state.posStations.selectedStation?.stationName;

          const filteredShifts = data
            ?.filter((shift) => shift.workPeriod.stationName === stationName)
            ?.map((item) => ({ ...item.workPeriod }));

          if (!filteredShifts) return;

          dispatch(setPOSShifts(filteredShifts));
        },
        transformResponse: (
          response: IBaseApiRes<{ workPeriod: IPOSShift }[]>,
        ) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getAllShift: builder.query({
        query: (params) =>
          `shifts/?filter[stationID]=${params.stationID}&filter[salesDate]=${params.salesDate}&filter[cashier]=${params.cashier}`,
        providesTags: ['Shifts'],
        async onQueryStarted(_, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IPOSAllShift) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getActiveShift: builder.query({
        query: (params) =>
          `shifts/?filter[isActiveOnly]=${params.isActiveOnly}`,
        providesTags: ['Shifts'],
        async onQueryStarted(_, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IPOSAllShift) => {
          return response?.data;
        },
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getShiftID: builder.query({
        query: ({
          userID,
          stationID,
        }: {
          userID: string;
          stationID: string;
        }) => ({
          url: `shifts${userID ? `/${userID}` : ''}`,
          headers: {
            customStationID: stationID,
          },
        }),
        providesTags: ['Shift'],
        async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
          try {
            const { stationID } = arg;

            const { data } = await queryFulfilled;
            const state = getState() as RootState;

            const selectedRole =
              state?.posStations?.stations?.[stationID]?.selectedRole;

            if (data?.length <= 0 && selectedRole !== 'Waiter') {
              dispatch(clearPOSStation());
              dispatch(clearPOSMasterlist());
              dispatch(clearPOSTableArea());
              dispatch(onClose('posShiftAlert'));
              return;
            }

            if (!data) return;

            const currentShift = data?.find(
              (item) => item.stationRefID === stationID,
            );
            if (!currentShift) return;

            if (currentShift?.cutoffDate) {
              dispatch(
                setSalesDate({
                  code: 'POS',
                  salesDate: currentShift.cutoffDate,
                }),
              );
            }

            const station: StationEntry = {
              selectedRole: currentShift?.selectedRole ?? 'Cashier',
              shiftID: currentShift.shiftRefID,
              selectedShift: {
                stationName: currentShift.stationName,
                workPeriod: currentShift.workPeriod,
                workPeriodID: currentShift.workPeriodID.toString(),
              },
              selectedPrinter: currentShift.printerID,
            };

            dispatch(addPOSInitialShift({ stationID, station }));
            dispatch(onOpen('posShiftAlert'));
          } catch (error: any) {
            if (error?.error?.errMsg?.includes('No data was found')) {
              dispatch(clearPOSStation());
              dispatch(clearPOSMasterlist());
              dispatch(clearPOSTableArea());
              dispatch(onClose('posShiftAlert'));
            }
          } finally {
            // (async () =>
            //   await dispatch(
            //     userManagementApi.endpoints.getUserPrivilege.initiate({
            //       roleID: '',
            //     }),
            //   ).unwrap())();
          }
        },
        keepUnusedDataFor: 0,
        forceRefetch: () => true,
        transformResponse: (
          response: IBaseApiRes<{ results: IPOSGetShiftID[] }>,
        ) => response?.data?.results,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postStartShift: builder.mutation({
        query: ({
          workPeriodID,
          payload,
          printerRefID,
        }: {
          workPeriodID: string;
          printerRefID?: string;
          payload: IPOSPostStartShift;
        }) => ({
          url: `shifts/${workPeriodID}/printer${printerRefID ? `/${printerRefID}` : ''}`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(posWorkPeriodAPI.util.invalidateTags(['Shifts']));
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: { shiftID: string } }) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      putEndShift: builder.mutation({
        query: ({
          shiftID,
          payload,
        }: {
          payload: IPOSPostEndShift;
          shiftID: string;
          stationID: string; // for removing station shift details
        }) => ({
          url: `shifts/${shiftID}/close`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(posWorkPeriodAPI.util.invalidateTags(['Shifts']));
              dispatch(removePOSStationEntry(arg.stationID));
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      // #region DECLARE NEW DAY
      postPOSDeclareNewDay: builder.mutation({
        query: (payload: IPOSDeclareNewDay) => ({
          url: 'declare-new-day/pos',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(posWorkPeriodAPI.util.invalidateTags(['Shifts']));
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: { shiftID: string } }) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      getPOSDeclareNewDay: builder.query({
        query: (salesDate: string) => ({
          url: 'declare-new-day/pos',
          headers: {
            salesDate,
          },
        }),
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        keepUnusedDataFor: 0,
        forceRefetch: () => true,
        transformResponse: (
          response: IBaseApiRes<{
            responseCode: string;
            responseMessage: string;
          }>,
        ) => response?.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      // #endregion
    }),
  });

// #region Export Hooks
export const {
  usePutEndShiftMutation: usePutEndShift,
  useGetShiftQuery: useGetShift,
  useGetAllShiftQuery: useGetAllShift,
  useGetActiveShiftQuery: useGetActiveOnlyShift,
  useLazyGetAllShiftQuery: useLazyGetAllShift,
  usePostStartShiftMutation: usePostStartShift,
  useGetShiftIDQuery: useGetShiftID,
  useLazyGetShiftIDQuery: useLazyGetShiftID,
  usePostPOSDeclareNewDayMutation: usePostPOSDeclareNewDay,
  useLazyGetPOSDeclareNewDayQuery: useLazyGetPOSDeclareNewDay,
} = posWorkPeriodAPI;
// #endregion Export Hooks
